/** Launch & mapping → External IDs (plan §2 S1 "Identifiers table"): one row per ID the
 *  tour is matched by — the primary `custom_id` first (emphasised), then each alias — and
 *  the System block under it (the tour's UUID, link, code and its last 20 import runs).
 *
 *  - Columns: the value — with which pipelines use it (the ext-IDs index facets of the
 *    stored value) as its second line — its type, the booking system and account it
 *    belongs to (picked from the operator's configured systems, never typed), Matching,
 *    API. Sized for a ~720 px panel. A row's comment,
 *    link, tags on match and "also books" tours are in its details row (FieldSlots).
 *  - Rows are draft records (`alias:<id>`): "Add ID" is one undoable step that appends a
 *    new row (negative id) to the membership list; its value is required before Save. A
 *    removed stored row stays struck through until the save, with Restore.
 *  - A row's cell states put their words on a line under the row (`RowIssues`).
 *  - Who maintains an alias (`alias.managed`): an import-owned row reads "Managed" (with
 *    the import in its tooltip) and is read-only — no edit, no remove — until it is taken
 *    over. "Take over" (a line under the row, edit mode) is a draft step: the row is
 *    editable at once and the save applies it whole; it can be removed once the take-over
 *    is saved (a managed row the list leaves out is kept). An ops-owned saved row is
 *    handed back from its details row; the import then maintains it from its next sync.
 *    Both say what they set off on a line under the row, on the ledger row, and in the
 *    save confirmation.
 *  - The block is the `tour#mapping` anchor (band node 1); the System block the
 *    `tour#import` one (node 2). */
import AddIcon from '@mui/icons-material/Add';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { ExtIdFacet, ExtIdUsedBy } from '@/api/extIds';
import type { RestechAccountOption } from '@/api/tours';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { ManagedChip, managedSource } from '@/features/ext-ids/extIdFormat';
import { TAKE_OVER_EFFECT, handBackEffect } from '@/features/ext-ids/ownership';
import { BlockRule } from '@/features/records/panels/BlockRule';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import { importStatusLabel, IMPORT_STATE } from '@/features/import-runs/importRunFormat';
import { importIdActions } from '@/features/import-runs/importRunLinks';
import { addRow, removeRow, restoreRow, tableRows } from '@/features/records/collections/rows';
import { recordKey } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { BoolText, DraftCell, RowIssues, useCellValue } from '@/features/records/fields/DraftCell';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { CHANGED_TINT, FieldSlot } from '@/features/records/fields/FieldSlot';
import { HEADING_ROW_PX } from '@/features/records/panels/headingRow';
import { formatRelativeTime } from '@/utils/format';
import { importRunsHref } from '@/utils/gridLinks';

import { auxCustomIdActions, auxIntegrationActions, codeActions, customIdActions, externalUrlActions, uuidActions } from '../../tourLinks';
import { ALIAS_ENTITY, TOUR_ENTITY } from '../catalog';
import { ALIAS_MANAGED, COLLECTION_BY_ENTITY } from '../collections';
import { rowName } from '../editorConfig';
import { restechLabel, useRestechAccounts } from '../queries';
import { MISSING_PRIMARY } from '../readiness';
import { blockAnchorId } from '../registry';
import { useTourRecord } from '../TourRecordContext';

import { BlockAnchor } from './BlockAnchor';

const ALIASES = COLLECTION_BY_ENTITY.get(ALIAS_ENTITY)!;
const MEMBERSHIP = ALIASES.membership;
/** The details row's fields (FieldSlots). */
const DETAIL_PATHS = ['alias.comment', 'alias.ext_link', 'alias.tour_user_tags', 'alias.targets'] as const;

const COLS = [
  { key: 'value', label: 'ID · used by', width: '30%' },
  { key: 'type', label: 'Type', width: '18%' },
  { key: 'system', label: 'System · account', width: '20%' },
  { key: 'matching', label: 'Matching', width: '12%' },
  { key: 'api', label: 'API', width: '10%' },
  { key: 'actions', label: '', width: '10%' },
] as const;

const HEAD_SX = { py: 0.5, px: 0.75, fontSize: 12, color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' } as const;
const CELL_SX = { py: 0.25, px: 0.5, verticalAlign: 'middle' } as const;

export function IdentifiersBlock() {
  const { detail, links } = useTourRecord();
  const { mode, registerFocus, errorsFor } = useRecordEditor();
  const { state, dispatch, baselineOf } = useDraft();
  const editing = mode === 'edit';
  const tourRec = recordKey(TOUR_ENTITY, detail.id);
  const restechs = useRestechAccounts(detail.tenant?.id);
  const rows = tableRows(state, baselineOf, tourRec, ALIASES, editing);
  const canChange = editing && Boolean(detail.tenant) && !detail.is_deleted;
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  const toggle = (id: number, to?: boolean) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (to ?? !next.has(id)) next.add(id);
      else next.delete(id);
      return next;
    });

  // The membership list's ledger row lands on the table.
  useEffect(
    () =>
      registerFocus(tourRec, MEMBERSHIP, () => {
        const el = document.getElementById(blockAnchorId('mapping'));
        el?.scrollIntoView?.({ block: 'start' });
        el?.focus({ preventScroll: true });
      }),
    [registerFocus, tourRec],
  );

  const valueOf = (rec: string, path: string) => {
    const e = state.entries[rec]?.[path];
    if (e && editing) return e.to;
    const b = baselineOf(rec, path);
    return b === undefined ? ALIASES.defaults[path] : b;
  };
  const nameOf = (id: number) => rowName(recordKey(ALIAS_ENTITY, id), valueOf);

  const add = () => {
    const { id, action } = addRow(state, baselineOf, tourRec, ALIASES, { label: 'Add an external ID' });
    dispatch(action);
    requestAnimationFrame(() =>
      document
        .querySelector<HTMLInputElement>(`[data-rec="${recordKey(ALIAS_ENTITY, id)}"][data-cell="alias.custom_id"] input`)
        ?.focus(),
    );
  };
  const membershipErrors = editing ? errorsFor(tourRec, MEMBERSHIP) : [];
  const aliases = rows.filter((r) => !r.removed).length;
  // The server refuses a value listed twice (on the later position) whenever the list is
  // sent — i.e. once the draft changes any alias or the list: block it here first.
  const listSent =
    editing && (Boolean(state.entries[tourRec]?.[MEMBERSHIP]) || Object.keys(state.entries).some((r) => r.startsWith(`${ALIAS_ENTITY}:`)));
  const duplicates = listSent ? duplicateRows(rows.filter((r) => !r.removed).map((r) => ({ id: r.id, value: valueOf(recordKey(ALIAS_ENTITY, r.id), 'alias.custom_id') }))) : new Map<number, string>();

  return (
    <BlockAnchor id="mapping" label="External IDs">
      <Box sx={{ display: 'grid', gap: 0.75 }}>
        {/* The heading row holds Add ID at its end, its height reserved in both modes:
            entering edit mode moves nothing (Zero-Re-Layout). */}
        <Box data-block-heading sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: HEADING_ROW_PX, minWidth: 0 }}>
          <Typography variant="caption" component="h3" color="text.secondary" noWrap sx={{ m: 0, fontWeight: 600, minWidth: 0 }}>
            External IDs
            <Box component="span" sx={{ fontWeight: 400 }}>
              {' '}
              · {detail.custom_id ? 1 : 0} primary · {aliases} {aliases === 1 ? 'alias' : 'aliases'}
            </Box>
          </Typography>
          <Box sx={{ flex: 1 }} />
          {canChange && (
            <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ flex: 'none' }}>
              Add ID
            </Button>
          )}
        </Box>
        <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 600 }} aria-label="External IDs" data-identifiers-table>
            <colgroup>
              {COLS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {COLS.map((c) => (
                  <TableCell key={c.key} sx={HEAD_SX}>
                    {c.label || <Box component="span" sx={visuallyHidden}>Row actions</Box>}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <PrimaryRow />
              {rows.map(({ id, removed }) => (
                <AliasRow
                  key={id}
                  id={id}
                  name={nameOf(id)}
                  removed={removed}
                  duplicate={duplicates.get(id) ?? null}
                  open={open.has(id)}
                  onToggle={(to) => toggle(id, to)}
                  canChange={canChange}
                  restechs={restechs.data}
                  restechsLoading={restechs.isLoading}
                  links={links}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
        {restechs.isError && (
          <Typography variant="caption" color="error" data-load-error="restech-accounts" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
            Couldn’t load the operator’s booking systems — the System · account choices and their checks wait for them.
            <Button size="small" onClick={() => void restechs.refetch()} sx={{ py: 0, minHeight: 24 }}>
              Retry
            </Button>
          </Typography>
        )}
        {membershipErrors.map((e) => (
          <Typography key={e} variant="caption" color="error" data-row-issue="invalid">
            {e}
          </Typography>
        ))}
        <SystemBlock />
      </Box>
    </BlockAnchor>
  );
}

function PrimaryRow() {
  const { detail, links } = useTourRecord();
  const rec = recordKey(TOUR_ENTITY, detail.id);
  const { value } = useCellValue(rec, 'custom_id');
  const v = typeof value === 'string' ? value.trim() : '';
  return (
    <>
      <TableRow data-alias-row="primary" sx={{ bgcolor: 'action.hover' }}>
        <TableCell sx={CELL_SX}>
          <DraftCell
            rec={rec}
            path="custom_id"
            label="Primary ID"
            rowName="the tour"
            mono
            display={(x) =>
              typeof x === 'string' && x.trim() ? (
                <ValueActions actions={customIdActions(x.trim(), links)}>
                  <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.8125rem', fontWeight: 700 }}>
                    {x.trim()}
                  </Box>
                </ValueActions>
              ) : detail.import_summary.is_import_bound ? (
                <Typography
                  variant="body2"
                  component="span"
                  data-missing-primary
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'warning.dark', minWidth: 0 }}
                >
                  <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16, flex: 'none' }} />
                  <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {MISSING_PRIMARY}
                  </Box>
                </Typography>
              ) : (
                <Typography variant="body2" color="text.disabled" component="span">
                  None
                </Typography>
              )
            }
          />
          <UsedBy usedBy={detail.custom_id_used_by} stale={v !== (detail.custom_id ?? '')} />
        </TableCell>
        <TableCell sx={CELL_SX}>
          <Chip size="small" label="Primary" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
        </TableCell>
        <TableCell sx={CELL_SX}>
          <Typography variant="body2" color="text.secondary">
            Any system
          </Typography>
        </TableCell>
        <TableCell sx={CELL_SX}>
          <BoolText on={Boolean(v)} onText="always" offText="—" muted />
        </TableCell>
        <TableCell sx={CELL_SX}>
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        </TableCell>
        <TableCell sx={CELL_SX} />
      </TableRow>
      <IssuesRow cells={[{ rec, path: 'custom_id', label: 'Primary ID' }]} />
    </>
  );
}

function IssuesRow({ cells }: { cells: Parameters<typeof RowIssues>[0]['cells'] }) {
  const issues = <RowIssues cells={cells} />;
  return (
    <TableRow>
      <TableCell colSpan={COLS.length} sx={{ py: 0, px: 1, borderBottom: 0, '&:empty': { p: 0 } }}>
        {issues}
      </TableCell>
    </TableRow>
  );
}

/** Rows whose value an earlier row of the list already has (exact, trimmed — what the
 *  server compares): id → the reason. */
export function duplicateRows(rows: Array<{ id: number; value: unknown }>): Map<number, string> {
  const seen = new Set<string>();
  const out = new Map<number, string>();
  for (const { id, value } of rows) {
    const v = typeof value === 'string' ? value.trim() : '';
    if (!v) continue;
    if (seen.has(v)) out.set(id, `'${v}' is listed twice`);
    seen.add(v);
  }
  return out;
}

function AliasRow({
  id,
  name,
  removed,
  duplicate,
  open,
  onToggle,
  canChange,
  restechs,
  restechsLoading,
  links,
}: {
  id: number;
  name: string;
  removed: boolean;
  /** Why the value cannot be saved (listed twice), or null. */
  duplicate: string | null;
  open: boolean;
  onToggle: (to?: boolean) => void;
  canChange: boolean;
  restechs: RestechAccountOption[] | undefined;
  restechsLoading: boolean;
  links: { tenantId: number | null | undefined };
}) {
  const { detail } = useTourRecord();
  const { mode, registerFocus, config } = useRecordEditor();
  const { state, dispatch, baselineOf } = useDraft();
  const rec = recordKey(ALIAS_ENTITY, id);
  const tourRec = recordKey(TOUR_ENTITY, detail.id);
  const stored = detail.additional_custom_ids.find((a) => a.id === id) ?? null;
  const editing = mode === 'edit';
  const isNew = id < 0;
  const dirty = editing && Object.keys(state.entries[rec] ?? {}).length > 0;
  // Who maintains it: as saved, and as the draft has it (view mode: as saved).
  const storedManaged = !isNew && baselineOf(rec, ALIAS_MANAGED) === true;
  const managed = useCellValue(rec, ALIAS_MANAGED).value === true;
  const integration = useCellValue(rec, 'alias.integration').value;
  const account = useCellValue(rec, 'alias.restech_account').value;
  const owner = {
    integration: typeof integration === 'string' && integration ? integration : null,
    account: typeof account === 'string' && account ? account : null,
  };
  const setManaged = (to: boolean, label: string) =>
    dispatch({ type: 'SET', label, changes: [{ rec, path: ALIAS_MANAGED, from: baselineOf(rec, ALIAS_MANAGED), to }] });
  const undoOwnership = () =>
    dispatch({ type: 'REVERT', targets: [{ rec, path: ALIAS_MANAGED }, ...(config.cascadeTargets?.(rec, ALIAS_MANAGED, state) ?? [])] });

  // A ledger row for a details field opens the details first.
  useEffect(() => {
    const offs = DETAIL_PATHS.map((path) =>
      registerFocus(rec, path, () => {
        onToggle(true);
        requestAnimationFrame(() => {
          const slot = document.querySelector<HTMLElement>(`[data-field-slot="${path}"][data-rec="${rec}"]`);
          slot?.scrollIntoView?.({ block: 'center' });
          (slot?.querySelector<HTMLElement>('input, textarea, [role="combobox"]') ?? slot)?.focus({ preventScroll: true });
        });
      }),
    );
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFocus, rec, open]);

  // Struck through on the row's amber tint — never an opacity over readable text (P3's rule).
  const struck = removed ? { textDecoration: 'line-through' } : {};
  const cells = [
    { rec, path: 'alias.custom_id', label: 'ID' },
    { rec, path: 'alias.id_type', label: 'Type' },
    { rec, path: 'alias.integration', label: 'System' },
    { rec, path: 'alias.restech_account', label: 'Account' },
    { rec, path: 'alias.use_for_matching', label: 'Matching' },
    { rec, path: 'alias.api_queryable', label: 'API' },
  ];

  return (
    <>
      <TableRow
        data-alias-row={id}
        data-removed={removed || undefined}
        sx={[removed && { bgcolor: CHANGED_TINT }]}
      >
        <TableCell sx={{ ...CELL_SX, ...struck }}>
          {removed ? (
            <Typography variant="body2" sx={{ fontFamily: MONO, fontSize: '0.8125rem' }}>
              {name}
            </Typography>
          ) : (
            <DraftCell
              rec={rec}
              path="alias.custom_id"
              label="ID"
              rowName={name}
              mono
              required
              invalidReason={duplicate}
              display={(x) =>
                typeof x === 'string' && x.trim() ? (
                  <ValueActions actions={auxCustomIdActions(x.trim(), links)}>
                    <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.8125rem' }}>
                      {x}
                    </Box>
                  </ValueActions>
                ) : (
                  '—'
                )
              }
            />
          )}
          {!removed &&
            (isNew ? (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ minHeight: 18 }}>
                Used by: after saving
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
                <UsedBy usedBy={stored?.used_by ?? null} stale={dirty} />
                {managed && <ManagedChip integration={owner.integration} restechAccount={owner.account} dense />}
              </Box>
            ))}
        </TableCell>
        <TableCell sx={{ ...CELL_SX, ...struck }}>
          <DraftCell rec={rec} path="alias.id_type" label="Type" rowName={name} />
        </TableCell>
        <TableCell sx={{ ...CELL_SX, ...struck }}>
          <SystemAccountCell rec={rec} rowName={name} restechs={restechs} loading={restechsLoading} links={links} disabled={removed} />
        </TableCell>
        <TableCell sx={{ ...CELL_SX, ...struck }}>
          <DraftCell rec={rec} path="alias.use_for_matching" label="Matching" rowName={name} />
        </TableCell>
        <TableCell sx={{ ...CELL_SX, ...struck }}>
          <DraftCell rec={rec} path="alias.api_queryable" label="API" rowName={name} />
        </TableCell>
        <TableCell sx={{ ...CELL_SX, textAlign: 'right', whiteSpace: 'nowrap' }}>
          <Tooltip title={open ? 'Hide details' : 'Comment, link, tags, also books'}>
            <IconButton size="small" aria-expanded={open} aria-label={`Details of ${name}`} onClick={() => onToggle()}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {/* A saved managed row cannot be removed: the list leaving it out keeps it (its
              import would only recreate it). Take it over and save first. */}
          {canChange &&
            !storedManaged &&
            (removed ? (
              <Tooltip title="Restore — keep this ID">
                <IconButton
                  size="small"
                  aria-label={`Restore ${name}`}
                  onClick={() => dispatch(restoreRow(state, baselineOf, tourRec, ALIASES, id, `Restore ${name}`))}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={isNew ? 'Remove this new ID' : 'Remove — deleted on save'}>
                <IconButton
                  size="small"
                  aria-label={`Remove ${name}`}
                  onClick={() => dispatch(removeRow(state, baselineOf, tourRec, ALIASES, id, `Remove ${name}`))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}
        </TableCell>
      </TableRow>
      {removed && editing && (
        <TableRow>
          <TableCell colSpan={COLS.length} sx={{ py: 0.25, px: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <RemoveCircleOutlineIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />
              {name} is deleted when you save — Restore keeps it.
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {!removed && editing && !isNew && (storedManaged || managed) && (
        <OwnershipLine
          name={name}
          state={storedManaged ? (managed ? 'managed' : 'taking_over') : 'handing_back'}
          owner={owner}
          canChange={canChange}
          onTakeOver={() => setManaged(false, `Take over ${name}`)}
          onUndo={undoOwnership}
        />
      )}
      {!removed && <IssuesRow cells={cells} />}
      {open && (
        <TableRow data-alias-details={id}>
          <TableCell colSpan={COLS.length} sx={{ px: 1.5, py: 1, bgcolor: 'background.default' }}>
            <FieldCellGrid
              sx={FIELD_GRID_SX}
              cells={DETAIL_PATHS.map((path) => ({
                key: `${rec}:${path}`,
                rec,
                path,
                wide: path === 'alias.tour_user_tags' || path === 'alias.targets',
                node: (
                  <FieldSlot
                    fieldKey={path}
                    entity={ALIAS_ENTITY}
                    id={id}
                    path={path}
                    inlineChoices={false}
                    {...(path === 'alias.ext_link'
                      ? { actions: (v: unknown) => (typeof v === 'string' && /^https?:\/\//i.test(v.trim()) ? externalUrlActions(v.trim(), 'Open the link') : []) }
                      : {})}
                  />
                ),
              }))}
            />
            {!isNew && (
              <OwnershipDetail
                name={name}
                managed={managed}
                owner={owner}
                {...(canChange && !removed && !storedManaged && !managed
                  ? { onHandBack: () => setManaged(true, `Hand back ${name}`) }
                  : {})}
              />
            )}
            {!isNew && stored?.used_by && <UsedByDetail usedBy={stored.used_by} />}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

type Owner = { integration: string | null; account: string | null };

const LINE_BUTTON_SX = { py: 0, minHeight: 24, flex: 'none' } as const;

/** The line under a row an import maintains, or whose owner the draft changes (edit
 *  mode): what that means, and the one action it takes. */
function OwnershipLine({
  name,
  state,
  owner,
  canChange,
  onTakeOver,
  onUndo,
}: {
  name: string;
  /** `managed`: saved as import-owned; `taking_over` / `handing_back`: the draft flips it. */
  state: 'managed' | 'taking_over' | 'handing_back';
  owner: Owner;
  canChange: boolean;
  onTakeOver: () => void;
  onUndo: () => void;
}) {
  let icon: ReactNode;
  let words: string;
  let action: ReactNode = null;
  if (state === 'managed') {
    icon = <SyncIcon aria-hidden sx={{ fontSize: 14, color: 'text.secondary' }} />;
    words = `Maintained by an import (${managedSource(owner.integration, owner.account)}) — read-only here until you take it over.`;
    if (canChange) {
      action = (
        <Button size="small" variant="outlined" sx={LINE_BUTTON_SX} onClick={onTakeOver} aria-label={`Take over ${name}`}>
          Take over
        </Button>
      );
    }
  } else {
    icon =
      state === 'taking_over' ? (
        <LockOpenIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />
      ) : (
        <SyncIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />
      );
    words =
      state === 'taking_over'
        ? `Taken over when you save: ${TAKE_OVER_EFFECT}. Edit it now; remove it once saved.`
        : `Handed back when you save. ${handBackEffect(owner.integration, owner.account)}.`;
    if (canChange) {
      action = (
        <Button size="small" sx={LINE_BUTTON_SX} onClick={onUndo} aria-label={`Undo — ${name} stays ${state === 'taking_over' ? 'with its import' : 'yours'}`}>
          Undo
        </Button>
      );
    }
  }
  return (
    <TableRow data-ownership-line={state}>
      <TableCell colSpan={COLS.length} sx={{ py: 0.25, px: 1, borderBottom: 0 }}>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
          {icon}
          <span>{words}</span>
          {action}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/** The details row's "Maintained by": an import or ops — and, for a saved row ops own,
 *  the way to hand it back to the import. */
function OwnershipDetail({ name, managed, owner, onHandBack }: { name: string; managed: boolean; owner: Owner; onHandBack?: () => void }) {
  return (
    <Box data-ownership-detail sx={{ mt: 1, display: 'grid', gap: 0.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Maintained by
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {managed
            ? `An import (${managedSource(owner.integration, owner.account)}) — it updates this ID on every sync.`
            : 'Ops — no import changes or removes it.'}
        </Typography>
        {onHandBack && (
          <Tooltip title={`${handBackEffect(owner.integration, owner.account)}.`} describeChild>
            <Button size="small" variant="outlined" startIcon={<SyncIcon />} sx={LINE_BUTTON_SX} onClick={onHandBack} aria-label={`Hand back to import — ${name}`}>
              Hand back to import
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

/** The booking system and account of an alias: one choice, two paths, one undo step. */
function SystemAccountCell({
  rec,
  rowName: name,
  restechs,
  loading,
  links,
  disabled,
}: {
  rec: string;
  rowName: string;
  restechs: RestechAccountOption[] | undefined;
  /** The list is loading (a failed load is said once, under the table). */
  loading: boolean;
  links: { tenantId: number | null | undefined };
  disabled: boolean;
}) {
  const { config, registerFocus, errorsFor } = useRecordEditor();
  const { state, dispatch, baselineOf } = useDraft();
  const integ = useCellValue(rec, 'alias.integration');
  const acct = useCellValue(rec, 'alias.restech_account');
  const integration = typeof integ.value === 'string' && integ.value ? integ.value : null;
  const account = typeof acct.value === 'string' && acct.value ? acct.value : null;
  const label = restechLabel(restechs, integration, account);
  const editing = integ.editing;
  const field = config.fields.find((f) => f.key === 'alias.integration');
  const editable = Boolean(field) && (config.editabilityOf?.(rec, field!, state) ?? { editable: true }).editable;
  const changed = integ.changed || acct.changed;
  const errors = editing ? [...errorsFor(rec, 'alias.integration'), ...errorsFor(rec, 'alias.restech_account')] : [];
  const [input, setInput] = useState<HTMLInputElement | null>(null);

  useEffect(() => {
    const focus = () => input?.focus();
    const a = registerFocus(rec, 'alias.integration', focus);
    const b = registerFocus(rec, 'alias.restech_account', focus);
    return () => {
      a();
      b();
    };
  }, [registerFocus, rec, input]);

  const ANY: RestechAccountOption = { id: '', name: 'Any system', code: '', account: null };
  const options: RestechAccountOption[] = [ANY, ...(restechs ?? [])];
  const current =
    integration === null
      ? ANY
      : (options.find((o) => o.code === integration && o.account === account) ?? {
          id: `?${integration}:${account ?? ''}`,
          name: `${label.text} (not configured)`,
          code: integration,
          account,
        });
  if (!options.some((o) => o.id === current.id)) options.push(current);

  const fromOf = (path: string) => {
    const b = baselineOf(rec, path);
    return b === undefined ? config.defaultValueOf?.(rec, path) : b;
  };

  const staleEntry = state.entries[rec]?.['alias.integration']?.stale;
  const showText = !editing || !editable || disabled;
  const text = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {label.known === false && (
        <Tooltip title="This operator has no such booking system or account configured">
          <WarningAmberIcon aria-label="Not configured for this operator" sx={{ fontSize: 16, color: 'warning.main' }} />
        </Tooltip>
      )}
      <Typography variant="body2" noWrap color={integration ? 'text.primary' : 'text.secondary'} sx={{ minWidth: 0 }}>
        {label.text}
      </Typography>
    </Box>
  );
  // One cell in both modes — the same wrapper, the revert room reserved (Zero-Re-Layout).
  return (
    <Box
      data-cell="alias.integration"
      data-rec={rec}
      data-state={changed ? 'changed' : undefined}
      sx={[
        { display: 'flex', alignItems: 'center', gap: 0.25, borderRadius: 1, px: 0.25, minHeight: 32, minWidth: 0 },
        changed && { bgcolor: CHANGED_TINT },
      ]}
    >
      <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center' }}>
        {showText ? (
          integration ? (
            <ValueActions actions={auxIntegrationActions({ integration, restech_account: account }, links)}>{text}</ValueActions>
          ) : (
            text
          )
        ) : (
          <Autocomplete<RestechAccountOption, false, true, false>
            size="small"
            fullWidth
            disableClearable
            options={options}
            value={current}
            loading={loading}
            getOptionLabel={(o) => o.name}
            getOptionKey={(o) => o.id}
            isOptionEqualToValue={(x, y) => x.id === y.id}
            renderOption={(props, o) => {
              const { key, ...rest } = props as typeof props & { key: string };
              return (
                <li key={key} {...rest}>
                  <Box sx={{ display: 'grid' }}>
                    <span>{o.name}</span>
                    {o.code && (
                      <Typography variant="caption" color="text.secondary">
                        {o.code}
                        {o.account ? `:${o.account}` : ' · default account'}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            onChange={(_, o) =>
              dispatch({
                type: 'SET',
                label: `${name}: ${o.code ? o.name : 'any system'}`,
                changes: [
                  { rec, path: 'alias.integration', from: fromOf('alias.integration'), to: o.code || null },
                  { rec, path: 'alias.restech_account', from: fromOf('alias.restech_account'), to: o.code ? o.account : null },
                ],
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                error={errors.length > 0}
                inputRef={setInput}
                inputProps={{ ...params.inputProps, 'aria-label': `System and account of ${name}` }}
                sx={{ '& .MuiInputBase-root': { py: '1px !important', fontSize: '0.875rem' } }}
              />
            )}
          />
        )}
      </Box>
      {staleEntry && <WarningAmberIcon aria-label="Changed on the server" sx={{ fontSize: 16, color: 'warning.main', flex: 'none' }} />}
      <Box sx={{ width: 22, flex: 'none', display: 'flex', justifyContent: 'center' }}>
        {changed && !showText && (
          <Tooltip title="Revert the system and account">
            <IconButton
              size="small"
              aria-label={`Revert System and account of ${name}`}
              onClick={() =>
                dispatch({
                  type: 'REVERT',
                  targets: [
                    { rec, path: 'alias.integration' },
                    { rec, path: 'alias.restech_account' },
                  ],
                })
              }
              sx={{ p: 0.25 }}
            >
              <RestoreIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

const FACETS: Array<{ key: keyof ExtIdUsedBy; label: string }> = [
  { key: 'import', label: 'Import' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'api', label: 'API' },
];

function facetIcon(f: ExtIdFacet) {
  if (f.state === 'yes') return <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 14, color: 'primary.main' }} />;
  if (f.state === 'no') return <CancelOutlinedIcon aria-hidden sx={{ fontSize: 14, color: 'warning.dark' }} />;
  return <RemoveCircleOutlineIcon aria-hidden sx={{ fontSize: 14, color: 'text.disabled' }} />;
}

const FACET_WORD = { yes: 'uses it', no: 'cannot use it', na: 'not run by this operator' } as const;

/** Which pipelines use an ID: three marks, each a shape and a word (Import ✓). */
export function UsedBy({ usedBy, stale = false }: { usedBy: ExtIdUsedBy | null; stale?: boolean }) {
  if (!usedBy) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }
  // One tab stop and one tooltip for the three facets; the group's name lists them all.
  const words = FACETS.map(({ key, label }) => `${label}: ${FACET_WORD[usedBy[key].state]}`);
  const name = `Used by — ${words.join('; ')}${stale ? ' (the stored value — recomputed on save)' : ''}`;
  return (
    <Tooltip
      title={
        <Box sx={{ display: 'grid', gap: 0.25 }}>
          {FACETS.map(({ key, label }) => {
            const f = usedBy[key];
            return (
              <span key={key}>
                <b>{label}</b>: {FACET_WORD[f.state]}
                {f.detail ? ` — ${f.detail}` : ''}
              </span>
            );
          })}
          {stale && <span>The stored value; recomputed on save.</span>}
        </Box>
      }
    >
      <Box
        component="span"
        role="group"
        tabIndex={0}
        aria-label={name}
        data-used-by
        sx={{
          display: 'inline-flex',
          gap: 0.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          borderRadius: 0.5,
          outline: 'none',
          '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        }}
      >
        {FACETS.map(({ key, label }) => {
          const f = usedBy[key];
          return (
            <Box
              component="span"
              key={key}
              aria-hidden
              data-facet={key}
              data-facet-state={f.state}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: 11, color: f.state === 'no' ? 'warning.dark' : 'text.secondary' }}
            >
              {facetIcon(f)}
              {label}
            </Box>
          );
        })}
        {stale && (
          <Box component="span" aria-hidden sx={{ fontSize: 11, color: 'text.secondary' }}>
            · stored
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

function UsedByDetail({ usedBy }: { usedBy: ExtIdUsedBy }) {
  return (
    <Box sx={{ mt: 1, display: 'grid', gap: 0.25 }}>
      {FACETS.map(({ key, label }) => (
        <Typography key={key} variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {facetIcon(usedBy[key])}
          <b>{label}</b> {FACET_WORD[usedBy[key].state]}
          {usedBy[key].detail ? ` — ${usedBy[key].detail}` : ''}
        </Typography>
      ))}
    </Box>
  );
}

/** The tour's own identity and its import runs (read-only). */
function SystemBlock() {
  const { detail, links } = useTourRecord();
  const runs = detail.import_summary.runs ?? [];
  const failed = runs.filter((r) => r.state === IMPORT_STATE.FAILURE || r.state === IMPORT_STATE.PARTIAL_SUCCESS).length;
  return (
    <BlockAnchor id="import" label="System and imports">
      <BlockRule title="System" note="identity and imports, read-only">
        <Box sx={{ ...FIELD_GRID_SX, rowGap: 1 }}>
          <SystemValue label="Ext ID (UUID)">
            {detail.ext_id ? (
              <ValueActions actions={uuidActions(detail.ext_id)}>
                <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.8125rem', overflowWrap: 'anywhere' }}>
                  {detail.ext_id}
                </Box>
              </ValueActions>
            ) : null}
          </SystemValue>
          <SystemValue label="External link">
            {detail.ext_link ? (
              <ValueActions actions={externalUrlActions(detail.ext_link)}>
                <Link href={detail.ext_link} target="_blank" rel="noopener noreferrer" sx={{ overflowWrap: 'anywhere' }}>
                  {detail.ext_link}
                </Link>
              </ValueActions>
            ) : null}
          </SystemValue>
          <SystemValue label="Code">
            {detail.code ? <ValueActions actions={codeActions(detail.code)}>{detail.code}</ValueActions> : null}
          </SystemValue>
        </Box>
        <Box sx={{ display: 'grid', gap: 0.25 }} data-import-runs>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Import runs
            <Box component="span" sx={{ fontWeight: 400 }}>
              {runs.length === 0
                ? ' · none'
                : ` · last ${runs.length}${failed ? ` · ${failed} with errors` : ''} · `}
            </Box>
            {runs.length > 0 && (
              <Link component={RouterLink} to={importRunsHref([], links.tenantId ?? null)} variant="caption">
                all import runs
              </Link>
            )}
          </Typography>
          {runs.length > 0 && (
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25, maxHeight: 220, overflowY: 'auto' }}>
              {runs.map((r) => {
                const bad = r.state === IMPORT_STATE.FAILURE || r.state === IMPORT_STATE.PARTIAL_SUCCESS;
                return (
                  <Box component="li" key={r.id} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', fontSize: 13, minWidth: 0 }}>
                    <ValueActions actions={importIdActions(r.id, links)}>
                      <Link component={RouterLink} to={`/import-runs/${r.id}/info`} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {formatRelativeTime(r.created_ts)}
                      </Link>
                    </ValueActions>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                      {r.sources.map((s) => s.name || s.code).join(', ') || '—'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: 'inline-flex', gap: 0.25, alignItems: 'center', color: bad ? 'warning.dark' : 'text.secondary', whiteSpace: 'nowrap', ml: 'auto' }}
                    >
                      {bad && <WarningAmberIcon aria-hidden sx={{ fontSize: 14 }} />}
                      {importStatusLabel(r.state)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </BlockRule>
    </BlockAnchor>
  );
}

function SystemValue({ label, children }: { label: string; children: ReactNode }) {
  const empty = children === null || children === undefined;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" color={empty ? 'text.disabled' : 'text.secondary'}>
        {empty ? '—' : children}
      </Typography>
    </Box>
  );
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// px strings: in sx a bare `1` is 100%.
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
